import { Link } from "react-router-dom";
import { LogOut, Menu, X, Shield } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import logo from "@/assets/quirky-french-logo.webp";
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Navbar = () => {
  const { user, signOut } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);
  const visibleRef = useRef(true);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setScrolled(currentY > 8);
      let nowVisible = visibleRef.current;
      if (currentY <= 8) {
        nowVisible = true;
      } else if (currentY < lastScrollY.current) {
        nowVisible = true;
      } else if (currentY > lastScrollY.current + 5) {
        nowVisible = false;
      }
      if (nowVisible !== visibleRef.current) {
        visibleRef.current = nowVisible;
        setVisible(nowVisible);
        document.documentElement.style.setProperty('--navbar-offset', nowVisible ? '3.5rem' : '0px');
      }
      lastScrollY.current = currentY;
    };
    document.documentElement.style.setProperty('--navbar-offset', '3.5rem');
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) { setIsAdmin(false); return; }
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
    };
    checkAdminStatus();
  }, [user]);

  return (
    <nav className={`sticky top-0 z-[100] transition-all duration-300 bg-card/85 backdrop-blur-2xl border-b border-border/60 ${
      scrolled ? "shadow-nav" : ""
    } ${visible ? "translate-y-0" : "-translate-y-full"}`}>
      <div className="container mx-auto px-4 h-14 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3 hover:opacity-80 transition-opacity no-link-style">
          <img src={logo} alt="Quirky French" className="h-8" />
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-0.5">
          {!user && (
            <Link to="/" className="no-link-style">
              <Button variant="ghost" size="sm" className="text-[13px] font-medium h-8 px-3">{t("nav.home")}</Button>
            </Link>
          )}
          
          {user && (
            <>
              <Link to="/dashboard" className="no-link-style">
                <Button variant="ghost" size="sm" className="text-[13px] font-medium h-8 px-3">{t("nav.dashboard")}</Button>
              </Link>
              
              <Link to="/resources" className="no-link-style">
                <Button variant="ghost" size="sm" className="text-[13px] font-medium h-8 px-3">{t("nav.resources")}</Button>
              </Link>
              
              <Link to="/profile" className="no-link-style">
                <Button variant="ghost" size="sm" className="text-[13px] font-medium h-8 px-3">{t("nav.profile")}</Button>
              </Link>
              {isAdmin && (
                <Link to="/admin" className="no-link-style">
                  <Button variant="ghost" size="sm" className="text-[13px] font-medium h-8 px-3">
                    <Shield className="h-3.5 w-3.5 mr-1" />
                    Admin
                  </Button>
                </Link>
              )}
            </>
          )}
          
          {/* Language Switcher */}
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-border/60">
            <button
              onClick={() => setLanguage("en")}
              className={`text-lg hover:scale-110 transition-transform ${language === "en" ? "scale-110" : "opacity-40 hover:opacity-70"}`}
              title="English"
            >
              🇬🇧
            </button>
            <button
              onClick={() => setLanguage("fr")}
              className={`text-lg hover:scale-110 transition-transform ${language === "fr" ? "scale-110" : "opacity-40 hover:opacity-70"}`}
              title="Français"
            >
              🇫🇷
            </button>
          </div>
          
          {user ? (
            <Button variant="ghost" size="sm" onClick={signOut} className="ml-1.5 text-[13px] h-8">
              <LogOut className="h-3.5 w-3.5 mr-1" />
              {t("nav.signOut")}
            </Button>
          ) : (
            <Link to="/auth" className="no-link-style ml-1.5">
              <Button size="sm" className="h-8 px-4 text-[13px] rounded-full">
                {t("nav.signIn")}
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile */}
        <div className="flex md:hidden items-center gap-2">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setLanguage("en")}
              className={`text-lg hover:scale-110 transition-transform ${language === "en" ? "scale-110" : "opacity-40"}`}
              title="English"
            >🇬🇧</button>
            <button
              onClick={() => setLanguage("fr")}
              className={`text-lg hover:scale-110 transition-transform ${language === "fr" ? "scale-110" : "opacity-40"}`}
              title="Français"
            >🇫🇷</button>
          </div>
          
          <Button variant="ghost" size="sm" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="h-8 w-8 p-0">
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden px-4 pb-4">
          <div className="flex flex-col gap-0.5 bg-card rounded-xl p-2 border border-border shadow-elevated">
            {!user && (
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="no-link-style">
                <Button variant="ghost" size="sm" className="w-full justify-start text-[13px]">{t("nav.home")}</Button>
              </Link>
            )}
            {user && (
              <>
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="no-link-style">
                  <Button variant="ghost" size="sm" className="w-full justify-start text-[13px]">{t("nav.dashboard")}</Button>
                </Link>
                <Link to="/resources" onClick={() => setMobileMenuOpen(false)} className="no-link-style">
                  <Button variant="ghost" size="sm" className="w-full justify-start text-[13px]">{t("nav.resources")}</Button>
                </Link>
                <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="no-link-style">
                  <Button variant="ghost" size="sm" className="w-full justify-start text-[13px]">{t("nav.profile")}</Button>
                </Link>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="no-link-style">
                    <Button variant="ghost" size="sm" className="w-full justify-start text-[13px]">
                      <Shield className="h-3.5 w-3.5 mr-1.5" />
                      Admin
                    </Button>
                  </Link>
                )}
              </>
            )}
            <div className="border-t border-border/60 pt-1.5 mt-1">
              {user ? (
                <Button variant="ghost" size="sm" onClick={() => { signOut(); setMobileMenuOpen(false); }} className="w-full justify-start text-[13px]">
                  <LogOut className="h-3.5 w-3.5 mr-1.5" />
                  {t("nav.signOut")}
                </Button>
              ) : (
                <Link to="/auth" onClick={() => setMobileMenuOpen(false)} className="no-link-style">
                  <Button size="sm" className="w-full text-[13px]">{t("nav.signIn")}</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

